import { CreateOrUpdateTag, Tag } from '../types/tagTypes';

export type TagRow = {
    tag: Tag;
    depth: number;
    key: string;
};

export function buildTagRows(tags: Tag[]): TagRow[] {
    const tagById = new Map(tags.map((t) => [t.id, t]));
    const rows: TagRow[] = [];

    for (const tag of tags) {
        if (tag.parents.length !== 0) {
            continue;
        }

        if (tag.children.length !== 0) {
            rows.push({ tag, depth: 0, key: `root-${tag.id}` });

            tag.children.forEach((childId) => {
                const child = tagById.get(childId);

                if (!child) return;

                rows.push({
                    tag: child,
                    depth: 1,
                    key: `${tag.id}-${child.id}`,
                });
            });

            continue;
        }

        rows.push({ tag, depth: 0, key: `root-${tag.id}` });
    }

    return rows;
}

export type TagCopyEntry = {
    sourceTag: Tag;
    targetTag?: Tag;
    isNew: boolean;
    hasNameMismatch: boolean;
};

export type ResolveTargetTagIdsOps = {
    createTag: (body: CreateOrUpdateTag) => Promise<number>;
    updateTag: (
        targetTagId: number,
        body: CreateOrUpdateTag,
    ) => Promise<unknown>;
    /**
     * Resolves a source tag's icon to a media id valid in the target
     * environment (media ids are environment-specific). Return null to drop
     * the icon. When omitted, the raw source media id is used as-is.
     */
    copyIconFor?: (sourceTag: Tag) => Promise<number | null>;
};

export function mapTagIdsToStringIds(tagIds: number[], tags: Tag[]): string[] {
    const byId = new Map(tags.map((t) => [t.id, t]));
    return tagIds
        .map((id) => byId.get(id)?.stringId)
        .filter((stringId): stringId is string => Boolean(stringId));
}

export function hasTagsChanged(
    sourceTagIds: number[],
    targetTagIds: number[],
    sourceTags: Tag[],
    targetTags: Tag[],
) {
    const sourceStringIds = new Set(
        mapTagIdsToStringIds(sourceTagIds, sourceTags),
    );
    const targetStringIds = new Set(
        mapTagIdsToStringIds(targetTagIds, targetTags),
    );

    if (sourceStringIds.size !== targetStringIds.size) {
        return true;
    }

    for (const stringId of sourceStringIds) {
        if (!targetStringIds.has(stringId)) {
            return true;
        }
    }

    return false;
}

export function hasTagRelationsChanged(
    sourceTag: Tag,
    targetTag: Tag,
    sourceTags: Tag[],
    targetTags: Tag[],
) {
    return (
        hasTagsChanged(
            sourceTag.parents,
            targetTag.parents,
            sourceTags,
            targetTags,
        ) ||
        hasTagsChanged(
            sourceTag.children,
            targetTag.children,
            sourceTags,
            targetTags,
        )
    );
}

export function buildTagCopyEntry(
    sourceTag: Tag,
    targetTagsOrIndex: Tag[] | Map<string, Tag>,
): TagCopyEntry {
    const index =
        targetTagsOrIndex instanceof Map
            ? targetTagsOrIndex
            : new Map(targetTagsOrIndex.map((t) => [t.stringId, t]));
    const targetTag = index.get(sourceTag.stringId);
    const sourceNameEn = sourceTag.name.en?.trim() ?? '';
    const targetNameEn = targetTag?.name.en?.trim() ?? '';

    return {
        sourceTag,
        targetTag,
        isNew: !targetTag,
        hasNameMismatch: Boolean(targetTag && sourceNameEn !== targetNameEn),
    };
}

export function buildTagCopyPlan(
    sourceTagIds: number[],
    sourceTags: Tag[],
    targetTags: Tag[],
): TagCopyEntry[] {
    const sourceById = new Map(sourceTags.map((t) => [t.id, t]));
    const targetByStringId = new Map(targetTags.map((t) => [t.stringId, t]));

    return sourceTagIds
        .map((id) => sourceById.get(id))
        .filter((tag): tag is Tag => Boolean(tag))
        .map((sourceTag) => buildTagCopyEntry(sourceTag, targetByStringId));
}

/**
 * Resolves each source tag id to the equivalent tag id in the target environment,
 * creating any tags that don't exist yet.
 *
 * Parent / child relations are preserved across newly created tags: missing tags are
 * first created with empty relations (pass 1), then updated with the fully resolved
 * relation ids once every tag in the closure has an id (pass 2). This guarantees
 * `A -> B` and `B -> A` mutual references and DAG relations survive the copy,
 * regardless of the order in which tags appear in `sourceTagIds`.
 */
export async function resolveTargetTagIds(
    sourceTagIds: number[],
    sourceTags: Tag[],
    targetTags: Tag[],
    ops: ResolveTargetTagIdsOps,
): Promise<number[]> {
    const sourceById = new Map(sourceTags.map((t) => [t.id, t]));
    const targetByStringId = new Map(targetTags.map((t) => [t.stringId, t]));

    const closure = new Map<number, Tag>();
    const visit = (id: number) => {
        if (closure.has(id)) return;
        const tag = sourceById.get(id);
        if (!tag) return;
        closure.set(id, tag);
        if (targetByStringId.has(tag.stringId)) return;
        [...tag.parents, ...tag.children].forEach(visit);
    };
    sourceTagIds.forEach(visit);

    const missing = [...closure.values()].filter(
        (tag) => !targetByStringId.has(tag.stringId),
    );

    const iconIdsByStringId = new Map<string, number | null>();
    for (const sourceTag of missing) {
        let iconMediaId: number | null = null;
        if (sourceTag.iconMedia) {
            iconMediaId = ops.copyIconFor
                ? await ops.copyIconFor(sourceTag)
                : sourceTag.iconMedia.id;
        }
        iconIdsByStringId.set(sourceTag.stringId, iconMediaId);
        const newId = await ops.createTag({
            stringId: sourceTag.stringId,
            name: sourceTag.name,
            synonyms: sourceTag.synonyms,
            parents: [],
            children: [],
            iconMediaId,
        });
        targetByStringId.set(sourceTag.stringId, {
            ...sourceTag,
            id: newId,
            parents: [],
            children: [],
        });
    }

    const mapRelationsToTargetIds = (relationIds: number[]): number[] =>
        relationIds
            .map((id) => sourceById.get(id))
            .filter((tag): tag is Tag => Boolean(tag))
            .map((tag) => targetByStringId.get(tag.stringId)?.id)
            .filter((id): id is number => typeof id === 'number');

    for (const sourceTag of missing) {
        const created = targetByStringId.get(sourceTag.stringId);
        if (!created) continue;
        await ops.updateTag(created.id, {
            stringId: sourceTag.stringId,
            name: sourceTag.name,
            synonyms: sourceTag.synonyms,
            parents: mapRelationsToTargetIds(sourceTag.parents),
            children: mapRelationsToTargetIds(sourceTag.children),
            iconMediaId: iconIdsByStringId.get(sourceTag.stringId) ?? null,
        });
    }

    return sourceTagIds
        .map((id) => sourceById.get(id))
        .filter((tag): tag is Tag => Boolean(tag))
        .map((tag) => targetByStringId.get(tag.stringId)?.id)
        .filter((id): id is number => typeof id === 'number');
}
